import Header from "./Header";
import Footer from "../Footer";
import usePresence from "../../lib/usePresence";
import BlockRenderer from "../BlockRenderer";
import EmulationBanner from "../EmulationBanner";
import AnimatedBackground from "../AnimatedBackground";
import api from "../../lib/api";

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWidgetBuilder } from "../../context/WidgetBuilderContext";
import { GripVertical } from "lucide-react";


export default function Layout({ children }) {
  usePresence();
  const location = useLocation();
  const [bgType, setBgType] = useState(() => localStorage.getItem("idg_bg_type") || "cosmic_nebula");

  useEffect(() => {
    const handleBgChange = (e) => {
      if (e.detail) setBgType(e.detail);
    };
    window.addEventListener("idg:bg-changed", handleBgChange);
    return () => window.removeEventListener("idg:bg-changed", handleBgChange);
  }, []);

  // Live Online Stats Poller (Script 3)
  useEffect(() => {
    function onlineStats() {
      api.get("/widgets/live-metrics")
        .then(res => {
          const data = res.data;
          if (!data) return;
          const mods = data.server_mods || {};
          for (let mod in mods) {
            const el = document.getElementById(`mod_${mod}`);
            if (el) el.textContent = mods[mod];
          }
          const set = (id, val) => {
            const el = document.getElementById(id);
            if (el) el.textContent = val;
          };
          if (data.site) set('site_online', data.site);
          if (data.servers) set('servers_total', data.servers);
        })
        .catch(() => {});
    }
    onlineStats();
    const interval = setInterval(onlineStats, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Re-initialize IDG controllers on route change
    if (window.IDG && window.IDG.controllers) {
      Object.values(window.IDG.controllers).forEach(ctrl => {
        if (typeof ctrl.init === 'function') {
          ctrl.init();
        }
      });
    }
  }, [location.pathname]);

  const { user } = useAuth();
  const { isEditing, toggleEditing } = useWidgetBuilder();
  const isAdmin = user?.role === 'admin' || user?.role === 'root';

  // Completely standalone dedicated app for Admin Control Panel (ACP)
  if (location.pathname.startsWith("/admin")) {
    return (
      <div className="idg-admin-standalone-root min-h-screen bg-[#07090e] text-zinc-100 selection:bg-[var(--tf-primary)] selection:text-black">
        {children}
      </div>
    );
  }

  const isStandalonePage = location.pathname.startsWith("/u/") || location.pathname === "/vip" || location.pathname === "/loadout";

  return (
    <div className="idg-layout relative">
      <AnimatedBackground type={bgType} />
      <EmulationBanner />
      {isAdmin && !isStandalonePage && (
        <button 
          onClick={toggleEditing}
          className={`fixed left-0 top-1/2 -translate-y-1/2 z-[90] flex flex-col items-center justify-center px-2 py-4 rounded-r-lg shadow-2xl transition-all font-bold text-[10px] tracking-widest gap-2 border border-l-0 ${isEditing ? 'bg-[var(--tf-primary)] text-white border-[var(--tf-primary)]' : 'bg-black/80 backdrop-blur text-zinc-400 border-white/10 hover:text-white hover:bg-black'}`}
          style={{ writingMode: 'vertical-rl', transform: 'translateY(-50%) rotate(180deg)' }}
        >
          <GripVertical className="w-4 h-4 mb-1 opacity-50" />
          WIDGETS
        </button>
      )}

      <Header />
      {!isStandalonePage && <BlockRenderer position="header" />}
      <main className={`idg-main ${isStandalonePage ? 'p-0 max-w-none w-full' : 'idg-page'}`} data-testid="main-content">
        {!isStandalonePage && <BlockRenderer position="content_top" />}
        {children}
        {!isStandalonePage && <BlockRenderer position="content_bottom" />}
      </main>
      {!isStandalonePage && <BlockRenderer position="footer" />}
      <Footer />
    </div>
  );
}
